import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { getFilteredLenderFormsByLender } from '@core/util/lenderForms';
import { Matter, MatterFileType } from '@models/matter';
import {
	MatterFormData,
	PDFForms,
	PDFFormType,
} from '@models/matter-form-data';
import {
	MatterMortgage,
	MatterMortgageInstructionSource,
	MatterMortgageLenderForm,
	MatterMortgageType,
	TransactionType,
} from '@models/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterMortgageLenderFormsRepoService } from '@repos/matter-mortgage-lender-forms/matter-mortgage-lender-forms.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { Observable } from 'rxjs';
import { ICON } from '@core/util/icon';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { getMortgageLenders } from '@core/util/mortgage';
import { MortgageeType } from '@models/lender';

@UntilDestroy()
@Component({
	selector: 'app-forms',
	templateUrl: './forms.component.html',
	styleUrls: ['./forms.component.scss'],
	standalone: false,
})
export class FormsComponent implements OnInit {
	public ICON = ICON;
	public formTypes = PDFFormType;
	public baseRoute;
	public mortgagesUrl: string;
	public hideForms = false;
	public allMatterFormData: MatterFormData[] = [];
	mortgages: MatterMortgage[];
	public mortgagesWithForms: MatterMortgage[] = [];
	public lenderForms: MatterMortgageLenderForm[] = [];
	public PDFForms = PDFForms;
	public matterFileTypes = MatterFileType;

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@ViewSelectSnapshot(SelectedMatter.getSelected) matter: Matter;
	@Select(MatterMortgagesState.getAllMortgagesWithAssociations)
	mortgages$: Observable<MatterMortgage[]>;

	constructor(
		private navigationService: NavigationService,
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private matterMortgageLenderFormRepoService: MatterMortgageLenderFormsRepoService,
	) {
		this.baseRoute = UrlService.Matter_Documents_Forms(this.matterId);
	}

	ngOnInit(): void {
		this.mortgages$.pipe(untilDestroyed(this)).subscribe((mortgages) => {
			this.mortgages = mortgages;

			// Don't show form section for mortgages with private lenders AND LLC;
			this.mortgagesWithForms = mortgages.filter(
				(mortgage) =>
					!getMortgageLenders(mortgage).some(
						(lender) => lender.mortgageeType === MortgageeType.PRIVATE_LENDER,
					) &&
					mortgage.source !== MatterMortgageInstructionSource.LLC &&
					mortgage.type !== MatterMortgageType.LLC &&
					mortgage.transactionType !== TransactionType.VTB,
			);
			// we don't need to initialize or change forms if a form is already selected
			if (!this.activatedRoute.firstChild?.snapshot?.params?.formName) {
				switch (this.matter?.fileType) {
					case this.matterFileTypes.PURCHASE:
						this.navigationService.goToMatter_Documents_Forms_Form(
							this.matterId,
							PDFFormType.LTT_AFFIDAVIT_FORM,
						);
						break;
					case this.matterFileTypes.SALE:
						this.navigationService.goToMatter_Documents_Forms_Form(
							this.matterId,
							PDFFormType.ON_GST_NEW_HOUSING_REBATE,
						);
						break;
					case this.matterFileTypes.REFINANCE:
						this.setupRefinanceForms();
				}
			}
		});
	}

	setupRefinanceForms(): void {
		if (this.mortgages.length) {
			this.hideForms = false;
			const mortgageId = this.mortgages[0]?.id;
			this.matterMortgageLenderFormRepoService
				.All({ mortgageId })
				.pipe(untilDestroyed(this))
				.subscribe((lenderForms: Partial<MatterMortgageLenderForm>[]) => {
					// rome-ignore lint/suspicious/noExplicitAny: <explanation>
					this.lenderForms = getFilteredLenderFormsByLender(
						this.mortgages[0],
						lenderForms as any,
					);
					if (this.lenderForms.length) {
						this.navigationService.goToMatter_Documents_Mortgage_Forms(
							this.matterId,
							mortgageId,
							this.lenderForms[0].id,
						);
					}
				});
		} else {
			this.hideForms = true;
		}
	}

	navigateToMortgage(): void {
		void this.router.navigateByUrl(UrlService.Matter_Mortgages(this.matterId));
	}
}
